import { useWorkshopMode } from "../../../../hooks/useWorkshopMode";
import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { SecondaryButton } from "../../../components/Buttons/SecondaryButton";
import { OrgScreenShell } from "../../../components/OrgShell/OrgScreenShell";
import { SectionNavItem } from "../../../components/OrgShell/SectionNavItem";
import { BadgeChip } from "../../../components/OrgShell/BadgeChip";
import {
  FieldGrid,
  FieldGroup,
  SummaryRow,
} from "../../../components/OrgShell/FieldGroup";
import {
  CommissionSummaryCard,
  formatCommission,
} from "../../../components/commission/commission-summary-card.component";
import { CommissionFormWithVendor } from "../../../components/commission/commission-form-with-vendor.component";
import { InputWrapper } from "../../../components/Input/InputWrapper";
import {
  InputLabel,
  RequiredMark,
} from "../../../components/Input/InputLabel";
import { CustomInput } from "../../../components/Input/CustomInput";
import { CustomPhoneInput } from "../../../components/Input/CustomPhoneInput";
import {
  CountryAutoComplete,
  CountryAutoCompleteItem,
} from "../../../components/Input/CountryAutoComplete";
import Dropdown from "../../../components/dropdown/Dropdown";
import VATField from "../../../components/vat/VATField";
import LoadingElementIsolated from "../../../components/loading-element/loading-element-isolated";
import { useCountryContext } from "../../design-editor2/contexts/country.context";
import { CustomInputChangeEvent } from "../../../../utils/models/events/CustomInputChangeEvent";
import { useProfileContext } from "../../../../contexts/ProfileContext";
import { ShippingSection } from "../../customer-update/component/sections/shipping-section.component";
import { BrandsSection } from "../../customer-update/component/sections/brands-section.component";
import { useVisibleFieldErrors } from "../../customer-update/models/required-fields";
import { InputError } from "../../my-profile/InputError";
import { HidePricesToggle } from "./hide-prices-toggle.component";
import { CustomerForm } from "./customer.form";
import {
  CustomerCreateWizard_VM,
  WIZARD_STEPS,
} from "../vmHooks/useCustomerCreateFormViewModel";

/**
 * Wizard step labels are defined as plain constants in WIZARD_STEPS
 * (vmHooks/useCustomerCreateFormViewModel); they are translated here at the
 * render site, keyed by step ordinal.
 */
const STEP_LABEL_KEYS: Record<number, string> = {
  1: "customerCreate.company",
  2: "customerCreate.contactAndFirstUser",
  3: "customerCreate.pricing",
  4: "customerCreate.brands",
  5: "customerCreate.reviewAndCreate",
};

export function CustomerCreateWizard(vm: CustomerCreateWizard_VM) {
  const { t } = useTranslation();
  const {
    form,
    setForm,
    step,
    goToStep,
    goNext,
    goBack,
    maxVisitedStep,
    isFormValid,
    isLoading,
    onCreate,
    onCancel,
    shippingDiffers,
    setShippingDiffers,
  } = vm;

  const isLastStep = step === WIZARD_STEPS.length;

  return (
    <OrgScreenShell
      title={form.name || t("customerCreate.newCustomer")}
      subtitle={t("customerCreate.stepIndicator", {
        step,
        total: WIZARD_STEPS.length,
        label: t(STEP_LABEL_KEYS[step]),
      })!}
      avatarText={form.name ? undefined : "+"}
      badges={
        form.hidePrices ? (
          <BadgeChip tone="orange">{t("customerCreate.pricesHidden")}</BadgeChip>
        ) : undefined
      }
      nav={WIZARD_STEPS.map((wizardStep) => (
        <SectionNavItem
          key={wizardStep.ordinal}
          label={t(STEP_LABEL_KEYS[wizardStep.ordinal])}
          ordinal={wizardStep.ordinal}
          done={
            wizardStep.ordinal < step && vm.isStepValid(wizardStep.ordinal)
          }
          active={step === wizardStep.ordinal}
          disabled={wizardStep.ordinal > maxVisitedStep}
          onClick={() => goToStep(wizardStep.ordinal)}
        />
      ))}
      footer={{
        onCancel,
        onSave: isLastStep ? () => void onCreate().catch(() => {}) : goNext,
        saveLabel: isLastStep
          ? t("customerCreate.createCustomer")!
          : t("customerCreate.next")!,
        saveDisabled: isLastStep ? !isFormValid : false,
        error: step === 1 ? vm.validationSummary : undefined,
        extra:
          step > 1 ? (
            <SecondaryButton className="mr-4" onClick={goBack}>
              {t("back")}
            </SecondaryButton>
          ) : undefined,
      }}
      isLoading={isLoading}
    >
      {step === 1 && (
        <CompanyStep
          vm={vm}
          shippingDiffers={shippingDiffers}
          setShippingDiffers={setShippingDiffers}
        />
      )}
      {step === 2 && <ContactStep form={form} setForm={setForm} />}
      {step === 3 && <PricingStep vm={vm} />}
      {step === 4 && (
        <BrandsSection
          manufacturers={vm.manufacturers}
          isLoading={vm.isManufacturersLoading}
          excludedIds={form.excludedManufacturers.map((x) => x.id)}
          onChange={(ids) =>
            setForm({
              ...form,
              excludedManufacturers: (vm.manufacturers ?? []).filter((m) =>
                ids.includes(m.id)
              ),
            })
          }
        />
      )}
      {step === 5 && <ReviewStep vm={vm} />}
    </OrgScreenShell>
  );
}

// ----- Step 1: Company -----

function CompanyStep({
  vm,
  shippingDiffers,
  setShippingDiffers,
}: {
  vm: CustomerCreateWizard_VM;
  shippingDiffers: boolean;
  setShippingDiffers: (differs: boolean) => void;
}) {
  const { t } = useTranslation();
  const { form, setForm, showVendorPicker, vendors, selectedVendor } = vm;
  const { errorFor, handleBlur, markTouched } = useVisibleFieldErrors(
    vm.fieldErrors,
    vm.showFieldErrors
  );

  function handleInput(
    event: React.ChangeEvent<HTMLInputElement> | CustomInputChangeEvent
  ) {
    const target = event.target;
    setForm({ ...form, [target.name]: target.value });
  }

  function handleCountryChange(country: CountryAutoCompleteItem) {
    setForm({ ...form, country: country.code });
  }

  function errorClass(field: string): string | undefined {
    return errorFor(field) ? "!border-red-100" : undefined;
  }

  return (
    <div className="space-y-8">
      {showVendorPicker && vendors && (
        <FieldGroup
          title={t("vendor")}
          description={t("customerCreate.vendorPickerDescription")!}
        >
          <div className="w-72">
            <Dropdown
              selectedOption={selectedVendor}
              options={vendors}
              defaultTitle={t("pick-vendor")!}
              getOptionDisplay={(v) => v.name}
              onOptionSelect={(o) => {
                if (!o) return;
                setForm({ ...form, parentId: o.id });
              }}
            />
          </div>
        </FieldGroup>
      )}

      <FieldGroup title={t("customerCreate.companyDetails")}>
        <FieldGrid>
          <InputWrapper>
            <InputLabel>
              {t("customerCreate.companyName")}
              <RequiredMark />
            </InputLabel>
            <CustomInput
              name="name"
              value={form.name}
              onChange={handleInput}
              onBlur={handleBlur}
              className={errorClass("name")}
            />
            <InputError errorMessage={errorFor("name")} />
          </InputWrapper>
          <InputWrapper>
            <InputLabel>
              {t("customerCreate.cvr")}
              <RequiredMark />
            </InputLabel>
            <CustomInput
              name="cvr"
              value={form.cvr}
              onChange={handleInput}
              onBlur={handleBlur}
              className={errorClass("cvr")}
            />
            <InputError errorMessage={errorFor("cvr")} />
          </InputWrapper>
          <InputWrapper>
            <InputLabel>
              {t("customerCreate.companyEmail")}
              <RequiredMark />
            </InputLabel>
            <CustomInput
              type="email"
              name="email"
              value={form.email}
              onChange={handleInput}
              onBlur={handleBlur}
              className={errorClass("email")}
            />
            <InputError errorMessage={errorFor("email")} />
          </InputWrapper>
          <InputWrapper>
            <InputLabel>
              {t("customerCreate.companyPhone")}
              <RequiredMark />
            </InputLabel>
            <CustomPhoneInput
              name="phone"
              value={form.phone}
              onChange={handleInput}
              onBlur={handleBlur}
              disabled={false}
              className={errorClass("phone")}
            />
            <InputError errorMessage={errorFor("phone")} />
          </InputWrapper>
          <InputWrapper>
            {/* EORI is optional — no required mark, no validation. */}
            <InputLabel>{t("customerCreate.eori")}</InputLabel>
            <CustomInput name="eori" value={form.eori} onChange={handleInput} />
          </InputWrapper>
        </FieldGrid>
      </FieldGroup>

      <FieldGroup title={t("customerCreate.address")}>
        <FieldGrid>
          <InputWrapper>
            <InputLabel>
              {t("customerCreate.address")}
              <RequiredMark />
            </InputLabel>
            <CustomInput
              name="address"
              value={form.address}
              onChange={handleInput}
              onBlur={handleBlur}
              className={errorClass("address")}
            />
            <InputError errorMessage={errorFor("address")} />
          </InputWrapper>
          <div className="grid grid-cols-2 gap-x-4">
            <InputWrapper>
              <InputLabel>
                {t("customerCreate.postcode")}
                <RequiredMark />
              </InputLabel>
              <CustomInput
                name="postCode"
                value={form.postCode}
                onChange={handleInput}
                onBlur={handleBlur}
                className={errorClass("postCode")}
              />
              <InputError errorMessage={errorFor("postCode")} />
            </InputWrapper>
            <InputWrapper>
              <InputLabel>
                {t("customerCreate.city")}
                <RequiredMark />
              </InputLabel>
              <CustomInput
                name="city"
                value={form.city}
                onChange={handleInput}
                onBlur={handleBlur}
                className={errorClass("city")}
              />
              <InputError errorMessage={errorFor("city")} />
            </InputWrapper>
          </div>
          <InputWrapper>
            <InputLabel>
              {t("customerCreate.country")}
              <RequiredMark />
            </InputLabel>
            <div data-field="country" onBlur={() => markTouched("country")}>
              <CountryAutoComplete
                disabled={false}
                selectedCode={form.country}
                onCountryChange={handleCountryChange}
                error={!!errorFor("country")}
              />
            </div>
            <InputError errorMessage={errorFor("country")} />
          </InputWrapper>
        </FieldGrid>
      </FieldGroup>

      <ShippingSection
        form={form}
        onChange={(patch) => setForm({ ...form, ...patch } as CustomerForm)}
        differs={shippingDiffers}
        onDiffersChange={setShippingDiffers}
        required
        errors={vm.fieldErrors}
        showErrors={vm.showFieldErrors}
      />
    </div>
  );
}

// ----- Step 2: Contact & first user -----

function ContactStep({
  form,
  setForm,
}: {
  form: CustomerForm;
  setForm: (form: CustomerForm) => void;
}) {
  const { t } = useTranslation();

  function handleInput(
    event: React.ChangeEvent<HTMLInputElement> | CustomInputChangeEvent
  ) {
    const target = event.target;
    setForm({ ...form, [target.name]: target.value });
  }

  return (
    <div className="space-y-8">
      <FieldGroup
        title={t("customerCreate.primaryContactAndFirstUser")}
        description={t("customerCreate.primaryContactDescription")!}
      >
        <FieldGrid>
          <InputWrapper>
            <InputLabel>{t("customerCreate.contactPerson")}</InputLabel>
            <CustomInput
              name="contactPerson"
              value={form.contactPerson}
              onChange={handleInput}
            />
          </InputWrapper>
          <InputWrapper>
            <InputLabel>{t("customerCreate.contactEmail")}</InputLabel>
            <CustomInput
              type="email"
              name="contactEmail"
              value={form.contactEmail}
              onChange={handleInput}
            />
          </InputWrapper>
          <InputWrapper>
            <InputLabel>{t("customerCreate.contactPhone")}</InputLabel>
            <CustomPhoneInput
              name="contactPhone"
              value={form.contactPhone}
              onChange={handleInput}
              disabled={false}
            />
          </InputWrapper>
        </FieldGrid>
        <p className="text-xs text-gray-500">
          {t("customerCreate.addMoreUsersHint")}
        </p>
      </FieldGroup>
    </div>
  );
}

// ----- Step 3: Pricing -----

function PricingStep({ vm }: { vm: CustomerCreateWizard_VM }) {
  const { t } = useTranslation();
  const { form, setForm, showVendorPicker } = vm;
  const { profile } = useProfileContext();
  const [customizeOpen, setCustomizeOpen] = useState(false);
  const workshopMode = useWorkshopMode();

  if (workshopMode) {
    return (
      <p className="text-sm text-gray-500">
        {t("customerCreate.workshopPricingHidden")}
      </p>
    );
  }

  const commissionVendorId =
    form.parentId ?? profile?.userOrganizationInformations.organizationId;

  return (
    <div className="space-y-8">
      <FieldGroup
        title={t("customerCreate.commission")}
        description={t("customerCreate.commissionDescription")!}
      >
        {showVendorPicker && !form.parentId ? (
          <p className="text-xs text-gray-500">
            {t("customerCreate.selectVendorBeforeCommissions")}
          </p>
        ) : (
          <>
            <CommissionSummaryCard
              title={
                vm.isCommissionCustomized
                  ? t("customerCreate.customized")!
                  : t("customerCreate.yourDefaults")!
              }
              commission={vm.commissionProfile}
              action={
                <SecondaryButton onClick={() => setCustomizeOpen(!customizeOpen)}>
                  {customizeOpen ? t("close") : t("customerCreate.customize")}
                </SecondaryButton>
              }
            />
            {customizeOpen &&
              (vm.logoOptions ? (
                <div className="rounded-md border p-4">
                  <CommissionFormWithVendor
                    updateCommissions={vm.updateCommission}
                    logoOptions={vm.logoOptions}
                    commissionProfile={vm.commissionProfile}
                    userType={"customer"}
                    vendorId={commissionVendorId!}
                  />
                </div>
              ) : (
                <div className="relative h-24">
                  <LoadingElementIsolated transparent />
                </div>
              ))}
          </>
        )}
      </FieldGroup>

      <FieldGroup
        title={t("customerCreate.vat")}
        description={t("customerCreate.vatDescription")!}
      >
        <VATField
          value={form.vatRate ?? 0}
          onChange={(value) => setForm({ ...form, vatRate: value })}
        />
      </FieldGroup>

      <FieldGroup title={t("customerCreate.priceVisibility")}>
        <HidePricesToggle
          checked={form.hidePrices ?? false}
          onChange={(next) => setForm({ ...form, hidePrices: next })}
          className="flex flex-col space-y-1"
        />
      </FieldGroup>
    </div>
  );
}

// ----- Step 5: Review & create -----

function ReviewStep({ vm }: { vm: CustomerCreateWizard_VM }) {
  const { t } = useTranslation();
  const { form, goToStep, shippingDiffers } = vm;
  const { countries } = useCountryContext();

  function countryName(code?: string): string {
    if (!code) return "—";
    return countries.find((x) => x.code === code)?.name ?? code;
  }

  return (
    <div className="space-y-6">
      <p className="text-xs text-gray-500">
        {t("customerCreate.reviewIntro")}
      </p>

      <ReviewCard title={t("customerCreate.company")} onEdit={() => goToStep(1)}>
        <SummaryRow label={t("name")}>{form.name || "—"}</SummaryRow>
        <SummaryRow label={t("customerCreate.cvr")}>
          {form.cvr || "—"}
        </SummaryRow>
        <SummaryRow label={t("email")}>{form.email || "—"}</SummaryRow>
        <SummaryRow label={t("customerCreate.phone")}>
          {form.phone || "—"}
        </SummaryRow>
        <SummaryRow label={t("customerCreate.address")}>
          {[form.address, form.postCode, form.city]
            .filter(Boolean)
            .join(", ") || "—"}
        </SummaryRow>
        <SummaryRow label={t("customerCreate.country")}>
          {countryName(form.country)}
        </SummaryRow>
        {form.eori && (
          <SummaryRow label={t("customerCreate.eori")}>{form.eori}</SummaryRow>
        )}
        <SummaryRow label={t("customerCreate.shipping")}>
          {shippingDiffers
            ? [
                form.shippingName,
                form.shippingAddress,
                form.shippingPostCode,
                form.shippingCity,
                countryName(form.shippingCountry),
              ]
                .filter(Boolean)
                .join(", ") || "—"
            : t("customerCreate.sameAsCompanyAddress")}
        </SummaryRow>
      </ReviewCard>

      <ReviewCard
        title={t("customerCreate.contactAndFirstUser")}
        onEdit={() => goToStep(2)}
      >
        <SummaryRow label={t("customerCreate.contactPerson")}>
          {form.contactPerson || "—"}
        </SummaryRow>
        <SummaryRow label={t("email")}>{form.contactEmail || "—"}</SummaryRow>
        <SummaryRow label={t("customerCreate.phone")}>
          {form.contactPhone || "—"}
        </SummaryRow>
      </ReviewCard>

      <ReviewCard title={t("customerCreate.pricing")} onEdit={() => goToStep(3)}>
        <SummaryRow label={t("customerCreate.commission")}>
          {vm.isCommissionCustomized
            ? t("customerCreate.customized")
            : vm.vendorDefaultCommission
            ? t("customerCreate.yourDefaults")
            : t("customerCreate.none")}{" "}
          ({formatCommission(vm.commissionProfile?.overAll)}{" "}
          {t("customerCreate.overall")})
        </SummaryRow>
        <SummaryRow label={t("customerCreate.vat")}>
          {form.vatRate ?? 0}%
        </SummaryRow>
        <SummaryRow label={t("customerCreate.prices")}>
          {form.hidePrices ? (
            <BadgeChip tone="orange">
              {t("customerCreate.pricesHidden")}
            </BadgeChip>
          ) : (
            t("customerCreate.visible")
          )}
        </SummaryRow>
      </ReviewCard>

      <ReviewCard title={t("customerCreate.brands")} onEdit={() => goToStep(4)}>
        <SummaryRow label={t("customerCreate.excludedBrands")}>
          {form.excludedManufacturers.length > 0
            ? form.excludedManufacturers.map((x) => x.name).join(", ")
            : t("customerCreate.noneAllBrandsVisible")}
        </SummaryRow>
      </ReviewCard>
    </div>
  );
}

function ReviewCard({
  title,
  onEdit,
  children,
}: {
  title: string;
  onEdit: () => void;
  children: React.ReactNode;
}) {
  const { t } = useTranslation();
  return (
    <div className="rounded-md border bg-gray-150 p-4">
      <div className="mb-2 flex items-center">
        <h4 className="flex-1 text-sm font-semibold text-gray-700">{title}</h4>
        <button
          type="button"
          className="text-xs font-medium text-primary-100 hover:underline"
          onClick={onEdit}
        >
          {t("edit")}
        </button>
      </div>
      {children}
    </div>
  );
}
